import {
  Grid,
  Stack,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PermitCooldown,
  PermitFieldConfiguration,
  PermitNotification,
  PermitNotificationType,
  PermitType,
  UnitOfTime,
} from 'types';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { PermitTypesContext } from './PermitTypesPanel';
import { Status, EntityPanelActionHandler } from 'types';
import { useTranslation } from 'react-i18next';
import { useFetcher, useOutletContext, useLocation } from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import { useRouteIDs } from 'hooks/useRouteIDs';
import TextEditor from 'components/inputs/TextEditor';
import { AppContext } from 'context/appContext';
import TimeUnitSelect from 'components/inputs/TimeUnitSelect';
import { PermitCooldownWidget } from 'components/widgets/PermitCooldownWidget';
import DetailsHeader from 'components/layout/DetailsHeader';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaTextField from 'components/inputs/AuturaTextField';
import CurrencyInput from 'components/inputs/CurrencyInput';
import FormFrame from 'components/layout/FormFrame';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { ValidatorForm } from 'react-material-ui-form-validator';
import PermitFieldConfig from 'components/inputs/PermitFieldConfig';

export const PermitTypesForm = (): ReactNode => {
  const {
    displayMode,
    formMode,
    permitType,
    setPermitType,
    onBack,
    branding,
    serializedEditorState,
    setSerializedEditorState,
    fetcherKey,
  } = useOutletContext<PermitTypesContext>();
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const [notifications, setNotifications] = useState<PermitNotification[]>(
    permitType?.data.notifications || [],
  );
  const { segment2 } = useRouteIDs();
  const { pathname } = useLocation();
  const selfServiceUrl = `${window.PERMIT_CLIENT_URL}/selfService?entityType=${segment2.entityType}&entityID=${segment2.entityID}&permitTypeID=${permitType.id}`;
  const fetcher = useFetcher({ key: fetcherKey });
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('lg'));


  const paymentsEnabled = useMemo(() => {
    return (
      state.tenant.data.entityMerchant?.merchantID &&
      state.tenant.data.entityMerchant?.status === Status.Active
    );
  }, [state.tenant.data.entityMerchant]);

  // handle the browser back button
  useBackButton(onBack);

  useEffect(() => {
    // Initialize default fieldConfig if undefined to ensure backward compatibility
    if (!permitType.data.fieldConfig) {
      setPermitType({
        ...permitType,
        data: {
          ...permitType.data,
          fieldConfig: {
            vin: {
              enabled: false,
              required: false,
            },
            color: {
              enabled: true,
              required: false,
            },
            makeModel: {
              enabled: true,
              required: false,
            },
            driverLicense: {
              enabled: false,
              required: false,
            },
          },
        },
      });
    }
  }, [permitType.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'quantityOfSpots') {
      const numeric = value.replace(/[^0-9]/g, '');
      const quantityOfSpots = parseInt(numeric);
      setPermitType({
        ...permitType,
        data: { ...permitType.data, quantityOfSpots },
      });
    } else {
      setPermitType({ ...permitType, [name]: value });
    }
  };
  const handleQuantityOfSpotsAvailable = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        quantityOfSpots: Number(value),
      },
    });
  };
  const handleSelfServiceChange = (value: boolean) => {
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        selfService: value,
        needsApproval:
          !value ? false : permitType.data.needsApproval,
        cooldown:
          !value
            ? {
                unitOfTime:
                  permitType.data.cooldown?.unitOfTime || UnitOfTime.Forever,
                amountOfTime: permitType.data.cooldown?.amountOfTime || 0,
                enabled: false,
                target: permitType.data.cooldown?.target || 'vehicle',
                rate: permitType.data.cooldown?.rate || 0,
              }
            : permitType.data.cooldown,
      },
    });
  };
  const handleNeedsApprovalChange = (value: boolean) => {
    setPermitType({
      ...permitType,
      data: { ...permitType.data, needsApproval: value },
    });
  };
  const handleDefaultTermUnitChange = (value: UnitOfTime) => {
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        defaultTerm: {
          unitOfTime: value,
          amountOfTime: permitType.data.defaultTerm?.amountOfTime || 1,
        },
      },
    });
  };
  const handleDefaultTermAmountChange = (amountOfTime: number | string) => {
    const numeric = amountOfTime
      .toString()
      .replace(/[^0-9]/g, '')
      .trim();
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        defaultTerm: {
          unitOfTime:
            permitType.data.defaultTerm?.unitOfTime || UnitOfTime.Forever,
          amountOfTime: Number(numeric) || 0,
        },
      },
    });
  };
  const handleCooldownChange = (cooldown: PermitCooldown) => {
    setPermitType({
      ...permitType,
      data: { ...permitType.data, cooldown },
    });
  };
  const handleAmountOfTimeChange = (amountOfTime: number | string) => {
    const numeric = amountOfTime
      .toString()
      .replace(/[^0-9]/g, '')
      .trim();
    const renewalNudge = notifications.find(
      (n) => n.type === PermitNotificationType.RenewalNudge,
    );

    if (renewalNudge) {
      const _notifications = [
        ...notifications.filter(
          (n) => n.type !== PermitNotificationType.RenewalNudge,
        ),
        {
          ...renewalNudge,
          amountOfTime: Number(numeric) || 0,
        },
      ];
      setNotifications(_notifications);
      setPermitType({
        ...permitType,
        data: { ...permitType.data, notifications: _notifications },
      });
    }
  };
  const handleSendRenewalReminderChange = (value: boolean) => {
    let _notifications = [...notifications];
    if (!value) {
      _notifications = notifications.filter(
        (n) => n.type !== PermitNotificationType.RenewalNudge,
      );
    }
    if (
      !notifications.find((n) => n.type === PermitNotificationType.RenewalNudge)
    ) {
      _notifications.push({
        type: PermitNotificationType.RenewalNudge,
        amountOfTime: 0,
        unitOfTime: UnitOfTime.Week,
      });
    }

    setNotifications(_notifications);
    setPermitType({
      ...permitType,
      data: { ...permitType.data, notifications: _notifications },
    });
  };
  const handleSendRevokedNotificationChange = (value: boolean) => {
    let _notifications = [...notifications];
    if (!value) {
      _notifications = notifications.filter(
        (n) => n.type !== PermitNotificationType.Revoked,
      );
    } else if (
      !notifications.find((n) => n.type === PermitNotificationType.Revoked)
    ) {
      _notifications.push({ type: PermitNotificationType.Revoked });
    }
    setNotifications(_notifications);
    setPermitType({
      ...permitType,
      data: { ...permitType.data, notifications: _notifications },
    });
  };
  const handleSendExpirationNotificationChange = (value: boolean) => {
    let _notifications = [...notifications];
    if (!value) {
      _notifications = notifications.filter(
        (n) => n.type !== PermitNotificationType.Expiration,
      );
    } else if (
      !notifications.find((n) => n.type === PermitNotificationType.Expiration)
    ) {
      _notifications.push({ type: PermitNotificationType.Expiration });
    }
    setNotifications(_notifications);
    setPermitType({
      ...permitType,
      data: { ...permitType.data, notifications: _notifications },
    });
  };
  const handleUnitOfTimeChange = (value: UnitOfTime) => {
    const renewalNudge = notifications.find(
      (n) => n.type === PermitNotificationType.RenewalNudge,
    );
    if (renewalNudge) {
      const _notifications = [
        ...notifications.filter(
          (n) => n.type !== PermitNotificationType.RenewalNudge,
        ),
        {
          ...renewalNudge,
          unitOfTime: value,
        },
      ];
      setNotifications(_notifications);
      setPermitType({
        ...permitType,
        data: { ...permitType.data, notifications: _notifications },
      });
    }
  };
  const handleReadonlyClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!canEdit) e.stopPropagation();
  };
  const handlePriceChange = (amount: number) => {
    if (!paymentsEnabled) {
      amount = 0;
    }
    setPermitType({
      ...permitType,
      permitPricing: {
        id: permitType.permitPricingID || 0,
        tenantID: state.tenant.id,
        pricePerUnit: amount,
        status: Status.Active,
        maxAmountOfUnits: 0,
        name: `${permitType.name}`,
        unitOfTime:
          permitType.data.defaultTerm?.unitOfTime || UnitOfTime.Forever,
        permitFees: [],
      },
    });
  };

  const handleGuestPermitEnabledChange = (value: boolean) => {
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        guestPermitData: {
          enabled: value,
          term: permitType.data.guestPermitData?.term || {
            unitOfTime: UnitOfTime.Day,
            amountOfTime: 1,
          },
          rate: permitType.data.guestPermitData?.rate || 0,
          unitOfTime:
            permitType.data.guestPermitData?.unitOfTime || UnitOfTime.Year,
        },
      },
    });
  };
  const handleGuestPermitTermUnitChange = (value: UnitOfTime) => {
    if (value !== UnitOfTime.Forever) {
      setPermitType({
        ...permitType,
        data: {
          ...permitType.data,
          guestPermitData: {
            enabled: permitType.data.guestPermitData?.enabled || false,
            rate: permitType.data.guestPermitData?.rate || 0,
            unitOfTime:
              permitType.data.guestPermitData?.unitOfTime || UnitOfTime.Day,
            term: {
              unitOfTime: value,
              amountOfTime:
                permitType.data.guestPermitData?.term?.amountOfTime || 0,
            },
          },
        },
      });
    }
  };
  const handleGuestPermitTermAmountChange = (amountOfTime: number | string) => {
    const numeric = amountOfTime
      .toString()
      .replace(/[^0-9]/g, '')
      .trim();
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        guestPermitData: {
          enabled: permitType.data.guestPermitData?.enabled || false,
          rate: permitType.data.guestPermitData?.rate || 0,
          unitOfTime:
            permitType.data.guestPermitData?.unitOfTime || UnitOfTime.Month,
          term: {
            unitOfTime:
              permitType.data.guestPermitData?.term?.unitOfTime ||
              UnitOfTime.Month,
            amountOfTime: Number(numeric) || 0,
          },
        },
      },
    });
  };
  const handleGuestPermitRateUnitChange = (value: UnitOfTime) => {
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        guestPermitData: {
          enabled: permitType.data.guestPermitData?.enabled || false,
          rate: permitType.data.guestPermitData?.rate || 0,
          unitOfTime: value,
          term: {
            unitOfTime:
              permitType.data.guestPermitData?.term?.unitOfTime ||
              UnitOfTime.Month,
            amountOfTime:
              permitType.data.guestPermitData?.term?.amountOfTime || 0,
          },
        },
      },
    });
  };
  const handleGuestPermitRateAmountChange = (amountOfTime: number | string) => {
    const numeric = amountOfTime
      .toString()
      .replace(/[^0-9]/g, '')
      .trim();
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        guestPermitData: {
          enabled: permitType.data.guestPermitData?.enabled || false,
          rate: Number(numeric) || 0,
          unitOfTime:
            permitType.data.guestPermitData?.unitOfTime || UnitOfTime.Month,
          term: {
            unitOfTime:
              permitType.data.guestPermitData?.term?.unitOfTime ||
              UnitOfTime.Month,
            amountOfTime:
              permitType.data.guestPermitData?.term?.amountOfTime || 0,
          },
        },
      },
    });
  };

  const handleFieldConfigChange = (fieldConfig: PermitFieldConfiguration) => {
    setPermitType({
      ...permitType,
      data: {
        ...permitType.data,
        fieldConfig: fieldConfig,
      },
    });
  };

  const sendRenewalReminder = useMemo<boolean>(
    () =>
      !!notifications.find(
        (n) => n.type === PermitNotificationType.RenewalNudge,
      ),
    [notifications],
  );
  const sendExpirationNotification = useMemo<boolean>(
    () =>
      !!notifications.find((n) => n.type === PermitNotificationType.Expiration),
    [notifications],
  );
  const sendRevokedNotification = useMemo<boolean>(
    () =>
      !!notifications.find((n) => n.type === PermitNotificationType.Revoked),
    [notifications],
  );
  const unitOfTime = useMemo<UnitOfTime>(
    () =>
      notifications.find((n) => n.type === PermitNotificationType.RenewalNudge)
        ?.unitOfTime || UnitOfTime.Week,
    [notifications],
  );
  const canEdit = useMemo<boolean>(() => {
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    } else if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    return false;
  }, [state.entityPermissions, displayMode]);

  const title = useMemo<string>(() => {
    if (formMode === 'create') {
      return t('buttons.create', {
        name: t('permit_type.label', { ns: 'entities' }),
      });
    }
    return `${permitType.name}`;
  }, [formMode, t, permitType]);
  const submitLabel = useMemo<string>(() => {
    if (formMode === 'create') {
      return t('buttons.create', {
        name: t('permit_type.label', { ns: 'entities' }),
      });
    }
    return t('buttons.update', {
      name: t('permit_type.label', { ns: 'entities' }),
    });
  }, [formMode, t]);

  const handleSubmit = () => {
    if (formMode === 'create') {
      const newPermitType: PermitType = {
        ...permitType,
        permitPricingID: 0,
        data: {
          ...permitType.data,
          termsAndConditions: serializedEditorState,
        },
      };
      fetcher.submit(
        {
          body: JSON.stringify(newPermitType),
          handler:
            displayMode == 'property'
              ? EntityPanelActionHandler.PROPERTY_CREATE
              : EntityPanelActionHandler.GROUP_CREATE,
        },
        {
          method: 'POST',
          action: pathname,
          encType: 'application/json',
        },
      );
    }

    if (formMode === 'edit') {
      fetcher.submit(
        {
          body: JSON.stringify({
            ...permitType,
            permitPricingID: permitType?.permitPricingID || 0,
            data: {
              ...permitType.data,
              termsAndConditions: serializedEditorState,
            },
          }),
          handler: EntityPanelActionHandler.UPDATE,
        },
        {
          method: 'POST',
          action: pathname,
          encType: 'application/json',
        },
      );
    }
  };

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={title}
          submitLabel={submitLabel}
          onCancel={onBack}
          readonly={!canEdit}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer maxHeight='calc(100vh - 326px)'>
          <SettingsSection
            title={t('subheadings.details', {
              ns: 'permitTypes',
            })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='name'
                  label={t('permit_type.name', { ns: 'entities' })}
                  value={permitType.name}
                  onChange={handleChange}
                  required
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <AuturaTextField
                  name='description'
                  label={t('permit_type.description', { ns: 'entities' })}
                  value={permitType?.description || ''}
                  onChange={handleChange}
                  readonly={!canEdit}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='quantityOfSpots'
                  label={t('permit_type.data.quantityOfSpots', {
                    ns: 'entities',
                  })}
                  value={permitType.data?.quantityOfSpots || ''}
                  onChange={handleQuantityOfSpotsAvailable}
                  disabled={!canEdit}
                  helperText={t('helpers.quantity_of_spots', {
                    ns: 'permitTypes',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <TimeUnitSelect
                  label={t('permit_type.data.defaultTerm.label', {
                    ns: 'entities',
                  })}
                  unitOfTime={
                    permitType.data.defaultTerm?.unitOfTime ||
                    UnitOfTime.Forever
                  }
                  setUnitOfTime={handleDefaultTermUnitChange}
                  amountOfTime={permitType.data.defaultTerm?.amountOfTime || 1}
                  setAmountOfTime={handleDefaultTermAmountChange}
                  helperText={t('helpers.default_term', { ns: 'permitTypes' })}
                  disabled={!canEdit}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <CurrencyInput
                  name='pricePerUnit'
                  label={t('permit_type.permitPricing.pricePerUnit', {
                    ns: 'entities',
                  })}
                  value={permitType.permitPricing?.pricePerUnit || 0}
                  setValue={handlePriceChange}
                  readonly={!canEdit}
                  required
                  disabled={!paymentsEnabled}
                  helperText={
                    !paymentsEnabled
                      ? t('helpers.payments_disabled', { ns: 'permitTypes' })
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.notifications', {
              ns: 'permitTypes',
            })}
          >
            <Grid
              container
              width={'100%'}
              spacing={1.5}
              onClick={handleReadonlyClick}
              mt={-2}
            >
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  name='sendRevokedNotification'
                  label={t('permit_type.data.notifications.revoked', {
                    ns: 'entities',
                  })}
                  value={sendRevokedNotification}
                  setValue={handleSendRevokedNotificationChange}
                  helperText={t('helpers.revocation_notification', {
                    ns: 'permitTypes',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <AuturaSwitch
                  name='sendRenewalReminder'
                  label={t('permit_type.data.notifications.renewalNudge', {
                    ns: 'entities',
                  })}
                  value={sendRenewalReminder}
                  setValue={handleSendRenewalReminderChange}
                  helperText={t('helpers.renewal_reminder', {
                    ns: 'permitTypes',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <TimeUnitSelect
                  fullWidth
                  sx={{ minWidth: { xs: '100%', md: 300 } }}
                  label={t('permit_type.data.notifications.amountOfTime', {
                    ns: 'entities',
                  })}
                  required={sendRenewalReminder}
                  unitOfTime={unitOfTime}
                  setUnitOfTime={handleUnitOfTimeChange}
                  amountOfTime={
                    notifications.find(
                      (n) => n.type === PermitNotificationType.RenewalNudge,
                    )?.amountOfTime || 0
                  }
                  setAmountOfTime={handleAmountOfTimeChange}
                  disabled={!sendRenewalReminder}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4,
                }}
              >
                <AuturaSwitch
                  name='sendExpirationNotification'
                  label={t('permit_type.data.notifications.expiration', {
                    ns: 'entities',
                  })}
                  value={sendExpirationNotification}
                  setValue={handleSendExpirationNotificationChange}
                  helperText={t('helpers.expiration_notification', {
                    ns: 'permitTypes',
                  })}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.guest_permits', {
              ns: 'permitTypes',
            })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  name='guestPermitEnabled'
                  label={t('permit_type.data.guestPermitData.enabled', {
                    ns: 'entities',
                  })}
                  value={permitType.data.guestPermitData?.enabled || false}
                  setValue={handleGuestPermitEnabledChange}
                  helperText={t('helpers.guest_permits', {
                    ns: 'permitTypes',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <TimeUnitSelect
                  label={t('permit_type.data.guestPermitData.term.label', {
                    ns: 'entities',
                  })}
                  allowForever={false}
                  unitOfTime={
                    permitType.data.guestPermitData?.term?.unitOfTime ||
                    UnitOfTime.Month
                  }
                  setUnitOfTime={handleGuestPermitTermUnitChange}
                  amountOfTime={
                    permitType.data.guestPermitData?.term?.amountOfTime || 0
                  }
                  setAmountOfTime={handleGuestPermitTermAmountChange}
                  helperText={t('helpers.guest_permits_term', {
                    ns: 'permitTypes',
                  })}
                  disabled={!canEdit}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 3,
                }}
              >
                <TimeUnitSelect
                  rateMode={true}
                  label={t('labels.guest_permit_rate', { ns: 'permitTypes' })}
                  unitOfTime={
                    permitType.data.guestPermitData?.unitOfTime ||
                    UnitOfTime.Month
                  }
                  allowForever={false}
                  setUnitOfTime={handleGuestPermitRateUnitChange}
                  amountOfTime={permitType.data.guestPermitData?.rate || 0}
                  setAmountOfTime={handleGuestPermitRateAmountChange}
                  helperText={t('helpers.guest_permits_rate', {
                    ns: 'permitTypes',
                  })}
                  disabled={!canEdit}
                  required
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.field_config', {
              ns: 'permitTypes',
            })}
            subtitle={t('helpers.field_config', {
              ns: 'permitTypes',
            })}
            divider={true}
          >
            <PermitFieldConfig
              fieldConfig={permitType.data.fieldConfig || {}}
              onChange={handleFieldConfigChange}
              disabled={!canEdit}
            />
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.website', {
              ns: 'permitTypes',
            })}
            divider={false}
          >
            <Grid
              container
              spacing={{ xs: 3, md: 1.5 }}
              onClick={handleReadonlyClick}
              mt={-2}
            >
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <Stack spacing={2} display='flex' flexDirection='column'>
                <Box>
                    <AuturaSwitch
                      name='selfService'
                      label={t('permit_type.data.selfService.true', {
                        ns: 'entities',
                      })}
                      value={!!permitType.data.selfService}
                      setValue={handleSelfServiceChange}
                      readonly={!canEdit}
                      helperText={t('helpers.self_service', {
                        ns: 'permitTypes',
                      })}
                      link={permitType.data.selfService && formMode === 'edit' ? selfServiceUrl : ""}
                    />
                  </Box>
                  <Box>
                    <AuturaSwitch
                      name='needsApproval'
                      label={`${t('permit_type.data.needsApproval', {
                        ns: 'entities',
                      })} ${t('labels.self_service_only', {
                        ns: 'permitTypes',
                      })}`}
                      value={!!permitType.data.needsApproval}
                      setValue={handleNeedsApprovalChange}
                      readonly={!canEdit}
                      disabled={!permitType.data.selfService}
                      helperText={t('helpers.needs_approval', {
                        ns: 'permitTypes',
                      })}
                    />
                  </Box>
                </Stack>
              </Grid>
              {mobile && (
                <Grid size={12}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      align='center'
                    >
                      {t('helpers.mobile_features', { ns: 'permitTypes' })}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {!mobile && (
                <Grid
                  size={{
                    xs: 12,
                    lg: 6,
                  }}
                >
                  <PermitCooldownWidget
                    cooldown={permitType.data.cooldown}
                    setCooldown={handleCooldownChange}
                    readonly={!canEdit}
                    disabled={!permitType.data.selfService}
                  />
                </Grid>
              )}
              {!mobile && (
                <Grid size={12}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      mt: 4,
                    }}
                  >
                    <div>
                      <TextEditor
                        serializedEditorState={serializedEditorState}
                        setSerializedEditorState={setSerializedEditorState}
                        branding={branding}
                        canEdit={canEdit}
                      />
                    </div>
                  </Box>
                </Grid>
              )}
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
